import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { cn, DropdownChevron, DropdownPanel, Icons } from '@workflow/ui'

/**
 * A spreadsheet's dropdown chip: the value with a small chevron inside the
 * cell, and the choices in a list that floats over the grid — drawn at the
 * window's level so the grid's own scrolling never clips it.
 */
export function CellMenu<T extends string>({
  value,
  options,
  onChange,
  label,
  renderValue,
  disabled = false,
  className,
}: {
  value: T
  options: readonly { value: T; label: string; mark?: ReactNode }[]
  onChange: (value: T) => void
  label: string
  renderValue: (value: T) => ReactNode
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<{ left: number; top: number; above: boolean } | null>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !trigger.current) return
    const box = trigger.current.getBoundingClientRect()
    const height = options.length * 34 + 8
    const below = box.bottom + 4 + height < window.innerHeight
    setPlace({ left: Math.max(4, Math.min(box.left, window.innerWidth - 196)), top: below ? box.bottom + 4 : Math.max(4, box.top - height - 4), above: !below })
  }, [open, options.length])

  useEffect(() => {
    if (!open) return
    const close = (event: Event) => {
      if (event.type === 'keydown' && (event as KeyboardEvent).key !== 'Escape') return
      if (event.type === 'pointerdown' && (list.current?.contains(event.target as Node) || trigger.current?.contains(event.target as Node))) return
      setOpen(false)
      if (event.type === 'keydown') trigger.current?.focus()
    }
    const shut = () => setOpen(false)
    window.addEventListener('pointerdown', close)
    window.addEventListener('keydown', close)
    window.addEventListener('scroll', shut, true)
    window.addEventListener('resize', shut)
    return () => {
      window.removeEventListener('pointerdown', close)
      window.removeEventListener('keydown', close)
      window.removeEventListener('scroll', shut, true)
      window.removeEventListener('resize', shut)
    }
  }, [open])

  return (
    <>
      <button
        ref={trigger}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((current) => !current)
        }}
        className={cn(
          'inline-flex h-6 max-w-full items-center gap-1 rounded-full pr-1.5 pl-2.5 text-caption outline-none transition-colors duration-fast focus-visible:ring-1 focus-visible:ring-foreground disabled:opacity-60',
          className,
        )}
      >
        <span className="truncate">{renderValue(value)}</span>
        <DropdownChevron size="xs" open={open} />
      </button>
      {createPortal(
        <AnimatePresence>{open && place ? (
            <DropdownPanel
              above={place.above}
              ref={list}
              role="listbox"
              aria-label={label}
              style={{ left: place.left, top: place.top }}
              className="wf-frost fixed z-[200] w-48 rounded-xl border border-hairline p-1 shadow-overlay"
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => {
                    setOpen(false)
                    trigger.current?.focus()
                    if (option.value !== value) onChange(option.value)
                  }}
                  className={cn(
                    'flex h-[34px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-ui text-foreground outline-none transition-colors duration-fast hover:bg-tint focus-visible:bg-tint',
                    option.value === value && 'bg-selected',
                  )}
                >
                  {option.mark}
                  <span className="flex-1 truncate">{option.label}</span>
                  {option.value === value ? <Icons.check size="sm" /> : null}
                </button>
              ))}
            </DropdownPanel>
        ) : null}</AnimatePresence>,
        document.body,
      )}
    </>
  )
}
